<template>
  <wizzard-container :infos="infos">
    <div class="title">{{ $t('Form Location Title') }}</div>
    <p class="caption">{{ $t('Form Location Subtitle') }}</p>

    <v-row>
      <v-col cols="12">
        <vuetify-google-autocomplete
          id="business-location-map"
          ref="autocomplete"
          types="geocode"
          outlined
          dense
          :error-messages="autocompleteErrors"
          :placeholder="$t('Form Location Autocomplete Placeholder')"
          :hint="$t('Form Location Autocomplete Hint')"
          persistent-hint
          v-on:placechanged="getAddressData"
        />
      </v-col>
    </v-row>

    <div
      v-if="form.latitude && form.longitude"
      class="mt-5"
    >
      <GmapMap
        ref="mapRef"
        :center="{ lat: form.latitude, lng: form.longitude }"
        :zoom="16"
        style="width: 100%; height: 250px"
        @dragend="dragend"
      />
      <p class="caption">{{ $t('Form Location drag and drop pin to exact location') }}</p>
    </div>

    <form-action-buttons
      status="listed"
      @submit="submit"
    />
  </wizzard-container>
</template>

<style lang="scss">
.vue-map:after {
  display: block;
  content: ' ';
  position: absolute;
  top: 50%;
  left: 50%;
  background: url('~@/assets/icons/map_pin_home2.svg');
  pointer-events: none;
  width: 40px;
  height: 54px;
  margin: -54px 0 0 -20px;
  background-size: 40px 54px;
}
</style>

<script>
import axios from 'axios';
import FormValidationMixin from '@/lib/FormValidationMixin';
import FormActionButtons from '@/views/ads/forms/FormActionButtons.vue';
import WizzardContainer from '@/views/ads/forms/WizzardContainer.vue';

export default {
  mixins: [FormValidationMixin],
  components: {
    FormActionButtons,
    WizzardContainer,
  },
  props: {
    businessId: { type: String, required: true },
    business: { type: Object, default: null },
  },
  data() {
    return {
      form: {
        latitude: null,
        longitude: null,
        addressComponents: [],
        formattedAddress: null,
        shortFormattedAddress: null,
        adrFormatAddress: null,
        postalAddress: null,
      },
    };
  },
  computed: {
    autocompleteErrors() {
      if (!this.form.latitude || !this.form.longitude || !this.form.addressComponents.length) {
        return [this.$t('Form Location Street Error')];
      }

      return [];
    },
    infos() {
      return [
        {
          icon: 'green-energy',
          title: 'Business Form Location InfoTitle',
          description: 'Business Form Location InfoDescription',
        },
      ];
    },
  },
  watch: {
    business: {
      immediate: true,
      handler() {
        this.setFormData();
      },
    },
  },
  methods: {
    submit() {
      this.$store.dispatch('loading', true);
      axios
        .post(`panel/ads/validation/business/location/${this.businessId}`, this.form)
        .then(() => {
          this.$store.dispatch('message', this.$t('Saved successfully'));
          this.$emit('success');
        })
        .catch((error) => {
          this.$store.dispatch('setErrors', error.response.data.errors);
          this.scrollToFirstError();
        })
        .finally(() => {
          this.$store.dispatch('loading', false);
        });
    },
    getAddressData(addressData, placeResultData) {
      this.form.latitude = addressData.latitude;
      this.form.longitude = addressData.longitude;
      this.form.formattedAddress = placeResultData && placeResultData.formatted_address
        ? placeResultData.formatted_address
        : null;
      this.form.shortFormattedAddress = addressData && addressData.name
        ? addressData.name
        : this.form.formattedAddress;
      this.form.adrFormatAddress = placeResultData && placeResultData.adr_address
        ? placeResultData.adr_address
        : null;
      this.form.addressComponents = this.mapAddressComponents(placeResultData);
      this.form.postalAddress = this.mapPostalAddress(addressData);
    },
    mapAddressComponents(placeResultData) {
      if (!placeResultData || !Array.isArray(placeResultData.address_components)) {
        return [];
      }

      return placeResultData.address_components.map((component) => ({
        longText: component.long_name || null,
        shortText: component.short_name || null,
        types: component.types || [],
      }));
    },
    mapPostalAddress(addressData) {
      if (!addressData) {
        return null;
      }

      return {
        regionCode: addressData.country_code || null,
        locality: addressData.locality || null,
        postalCode: addressData.postal_code || null,
        addressLines: [addressData.name].filter(Boolean),
      };
    },
    dragend() {
      this.$refs.mapRef.$mapPromise.then((map) => {
        this.form.latitude = map.center.lat();
        this.form.longitude = map.center.lng();
      });
    },
    setFormData() {
      if (!this.business) {
        return;
      }

      this.form.latitude = this.business.latitude ? Number(this.business.latitude) : null;
      this.form.longitude = this.business.longitude ? Number(this.business.longitude) : null;
      this.form.addressComponents = this.business.location && this.business.location.addressComponents
        ? this.business.location.addressComponents
        : [];
      this.form.formattedAddress = this.business.location && this.business.location.formattedAddress
        ? this.business.location.formattedAddress
        : null;
      this.form.shortFormattedAddress = this.business.location && this.business.location.shortFormattedAddress
        ? this.business.location.shortFormattedAddress
        : null;
      this.form.adrFormatAddress = this.business.location && this.business.location.adrFormatAddress
        ? this.business.location.adrFormatAddress
        : null;
      this.form.postalAddress = this.business.location && this.business.location.postalAddress
        ? this.business.location.postalAddress
        : null;

      this.$nextTick(() => {
        if (this.$refs.autocomplete && this.form.formattedAddress) {
          this.$refs.autocomplete.update(this.form.formattedAddress);
        }
      });
    },
  },
};
</script>
